import { create } from 'zustand';

/**
 * Unsaved Changes Registry
 *
 * Phase 1 of the "auto-reload on new app version" feature.
 *
 * A tiny, ephemeral (non-persisted) registry that any editor in the app can
 * register itself with while it has unsaved local edits. The vanilla
 * inline-script auto-reload mechanism in `index.html` consults this (via
 * `window.__hasUnsavedChanges`, wired in `App.tsx`) before silently
 * reloading the page on a new service worker version — it must never wipe
 * out work a user hasn't saved yet.
 *
 * Editors register/unregister via the `useUnsavedGuard` hook
 * (`src/hooks/useUnsavedGuard.ts`). Phase 2 wires actual editors
 * (PostCreator, etc.) into this registry — this phase only builds the
 * mechanism.
 */
interface UnsavedChangesState {
  /** Map of editor instance id -> dirty flag. Only `true` entries matter. */
  dirtyIds: Map<string, boolean>;

  /** Mark (or unmark) a given editor instance as dirty. */
  setDirty: (id: string, dirty: boolean) => void;

  /** Remove an editor instance entirely (e.g. on unmount). */
  clear: (id: string) => void;

  /** True if any registered editor instance currently has unsaved changes. */
  hasUnsavedChanges: () => boolean;
}

export const useUnsavedChangesStore = create<UnsavedChangesState>((set, get) => ({
  dirtyIds: new Map(),

  setDirty: (id, dirty) =>
    set((state) => {
      const next = new Map(state.dirtyIds);
      if (dirty) {
        next.set(id, true);
      } else {
        next.delete(id);
      }
      return { dirtyIds: next };
    }),

  clear: (id) =>
    set((state) => {
      if (!state.dirtyIds.has(id)) return state;
      const next = new Map(state.dirtyIds);
      next.delete(id);
      return { dirtyIds: next };
    }),

  hasUnsavedChanges: () => get().dirtyIds.size > 0,
}));

/**
 * Non-hook accessor for use outside React (e.g. the `window.__hasUnsavedChanges`
 * bridge consumed by the vanilla inline script in `index.html`).
 */
export const hasUnsavedChanges = (): boolean =>
  useUnsavedChangesStore.getState().hasUnsavedChanges();
